import React from 'react';
import { Grid, Typography, useTheme } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import Tippy from '@tippyjs/react';
import { Bold } from '../../../components/Bold';
import { SemiBold } from '../../../components/SemiBold';
import { Spacer } from '../../../components';
import { HelpTitle, HelpBody } from '../../../components/HelpText';
import BulletedList from '../../../components/BulletedList';
import { formatNumber } from '../../../helpers/utilityFunctions';
import { type UserAccountSection } from '../../../data/report/helpers/userAccountsUtils';

interface SectionHeaderProps {
  count: number;
  label: string;
  helpContent: React.ReactNode;
  countVariant?: UserAccountSection['countVariant'];
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  count,
  label,
  helpContent,
  countVariant = 'p24',
}) => {
  const theme = useTheme();

  return (
    <Grid display='flex' alignItems='center' mb={1}>
      <Grid display='flex' flexDirection='column'>
        <Typography variant={countVariant} color='secondary.main' fontWeight='bold'>
          {formatNumber(count)}
        </Typography>
        <Grid display='flex' alignItems='center' gap={1}>
          <Typography variant='p16' color='secondary.main'>
            <Bold>{label}</Bold> users
          </Typography>
          <Tippy placement='right' content={helpContent} theme='tippy-rounded'>
            <HelpOutline style={{ color: theme.palette.info.main, cursor: 'pointer' }} />
          </Tippy>
        </Grid>
      </Grid>
    </Grid>
  );
};

const HelpTextActive = () => {
  return (
    <Grid>
      <HelpTitle>Active users</HelpTitle>
      <Spacer height='xxs' />
      <HelpBody variant='p16Light'>These are accounts with an active license.</HelpBody>
      <Spacer height='xs' />
      <HelpBody variant='p16Light'>This includes but is not limited to the following:</HelpBody>
      <Spacer height='xxs' />
      <BulletedList>
        <Typography variant='p16Light' color='secondary.dark'>
          <SemiBold>In trial period:</SemiBold> users who have started a free trial from the mobile
          app
        </Typography>
        <Typography variant='p16Light' color='secondary.dark'>
          <SemiBold>Upgraded to add-on:</SemiBold> users who have an active license for an add-on
          package
        </Typography>
        <Typography variant='p16Light' color='secondary.dark'>
          <SemiBold>Unsubscribed from add-on:</SemiBold> users who no longer have an active license
          for an add-on package
        </Typography>
      </BulletedList>
    </Grid>
  );
};

const HelpTextPreSubscription = () => {
  return (
    <Grid>
      <HelpTitle>Pre-subscription users</HelpTitle>
      <Spacer height='xxs' />
      <HelpBody variant='p16Light'>These are accounts who have not finished signing up.</HelpBody>
      <Spacer height='xxs' />
      <BulletedList>
        <Typography variant='p16Light' color='secondary.dark'>
          <SemiBold>Invited:</SemiBold> emails that have been added but do not have an account
        </Typography>
        <Typography variant='p16Light' color='secondary.dark'>
          <SemiBold>Undecided:</SemiBold> users who created an account but have not chosen a product
        </Typography>
        <Typography variant='p16Light' color='secondary.dark'>
          <SemiBold>Abandoned:</SemiBold> users who started creating an account but did not finish
        </Typography>
      </BulletedList>
    </Grid>
  );
};

const HelpTextPostSubscription = () => {
  return (
    <Grid>
      <HelpTitle>Post-subscription users</HelpTitle>
      <Spacer height='xxs' />
      <HelpBody variant='p16Light'>
        These are accounts that no longer have an active license.
      </HelpBody>
      <Spacer height='xxs' />
      <BulletedList>
        <Typography variant='p16Light' color='secondary.dark'>
          <SemiBold>Ineligible:</SemiBold> users who have been removed from their organization
        </Typography>
        <Typography variant='p16Light' color='secondary.dark'>
          <SemiBold>Unsubscribed:</SemiBold> users who no longer have an active license
        </Typography>
      </BulletedList>
    </Grid>
  );
};

export const getSectionHelpContent = (label: string): React.ReactNode => {
  return {
    'Active': <HelpTextActive />,
    'Pre-subscription': <HelpTextPreSubscription />,
    'Post-subscription': <HelpTextPostSubscription />,
  }[label];
};

export default SectionHeader;
